import { supabase } from '../lib/supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Photos attached to a field quote (project_field_quotes). Stored in the shared
// `user-uploads` bucket under the `field-quote-photos/` folder.
// ─────────────────────────────────────────────────────────────────────────────

const BUCKET = 'user-uploads';

export interface FieldQuotePhoto {
  id: string;
  business_id: string;
  field_quote_id: string;
  file_name: string | null;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export function fieldQuotePhotoUrl(filePath: string): string {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(filePath);
  return data.publicUrl;
}

export interface FieldQuotePhotoGroup {
  fieldQuoteId: string;
  label: string;
  subLabel?: string;
  photos: FieldQuotePhoto[];
}

/**
 * Field-quote photos relevant to a proposal, grouped by field quote. Sources
 * field quotes linked to the CRM opportunity (lead) AND/OR belonging to the
 * customer, so photos are reachable even when the field quote isn't lead-linked.
 */
export async function getFieldQuotePhotoGroups(
  businessId: string,
  opts: { customerId?: string | null; opportunityId?: number | null },
): Promise<FieldQuotePhotoGroup[]> {
  const { customerId, opportunityId } = opts;
  if (!businessId || (!customerId && opportunityId == null)) return [];

  const ors: string[] = [];
  if (customerId) ors.push(`customer_id.eq.${customerId}`);
  if (opportunityId != null) ors.push(`pipeline_opportunities_id.eq.${opportunityId}`);

  const { data: fqs, error: fqErr } = await supabase
    .from('project_field_quotes')
    .select('id, field_quote_address, created_at, is_measurement')
    .eq('business_id', businessId)
    .or(ors.join(','))
    .order('created_at', { ascending: false });
  if (fqErr || !fqs?.length) return [];

  const ids = fqs.map((f) => f.id);
  const { data: photos, error } = await supabase
    .from('field_quote_photos')
    .select('*')
    .in('field_quote_id', ids)
    .order('created_at', { ascending: true });
  if (error) {
    console.error('Error loading field quote photos:', error);
    return [];
  }

  const byFq = new Map<string, FieldQuotePhoto[]>();
  (photos || []).forEach((p: FieldQuotePhoto) => {
    const arr = byFq.get(p.field_quote_id) || [];
    arr.push(p);
    byFq.set(p.field_quote_id, arr);
  });

  const groups: FieldQuotePhotoGroup[] = [];
  for (const fq of fqs as Array<{ id: string; field_quote_address: string | null; created_at: string | null; is_measurement: boolean | null }>) {
    const ph = byFq.get(fq.id);
    if (!ph?.length) continue;
    groups.push({
      fieldQuoteId: fq.id,
      label: fq.field_quote_address || (fq.is_measurement ? 'Field measurement' : 'Field quote'),
      subLabel: fq.created_at ? new Date(fq.created_at).toLocaleDateString() : undefined,
      photos: ph,
    });
  }
  return groups;
}

export async function listFieldQuotePhotos(fieldQuoteId: string): Promise<FieldQuotePhoto[]> {
  const { data, error } = await supabase
    .from('field_quote_photos')
    .select('*')
    .eq('field_quote_id', fieldQuoteId)
    .order('created_at', { ascending: true });
  if (error) {
    console.error('Error listing field quote photos:', error);
    return [];
  }
  return (data || []) as FieldQuotePhoto[];
}

export async function uploadFieldQuotePhoto(params: {
  businessId: string;
  fieldQuoteId: string;
  file: File;
}): Promise<FieldQuotePhoto> {
  const { businessId, fieldQuoteId, file } = params;
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const filePath = `field-quote-photos/${fieldQuoteId}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(filePath, file, { cacheControl: '3600', upsert: false });
  if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('field_quote_photos')
    .insert({
      business_id: businessId,
      field_quote_id: fieldQuoteId,
      file_name: file.name,
      file_path: filePath,
      file_size: file.size,
      mime_type: file.type,
      uploaded_by: user?.id || null,
    })
    .select()
    .single();

  if (error) {
    await supabase.storage.from(BUCKET).remove([filePath]);
    throw new Error(`Failed to save photo: ${error.message}`);
  }
  return data as FieldQuotePhoto;
}

export async function deleteFieldQuotePhoto(photoId: string): Promise<boolean> {
  const { data: photo } = await supabase
    .from('field_quote_photos')
    .select('file_path')
    .eq('id', photoId)
    .single();

  if (photo?.file_path) {
    await supabase.storage.from(BUCKET).remove([photo.file_path]);
  }
  const { error } = await supabase.from('field_quote_photos').delete().eq('id', photoId);
  if (error) {
    console.error('Error deleting field quote photo:', error);
    return false;
  }
  return true;
}
